import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react'
import { Drawer, Spin, Modal } from 'antd'
import useMessageChannel from 'gyl-common/components/MessageChannelIframe/hooks/useMessageChannel'
import styles from './index.less'
import { getToken } from '@/utils/token'
import { transImageToSursung } from '@/utils/file'
import { getCurrentModalMaxZindex } from '@/utils/modal'

const mode = PROJECT_CONFIG.sentryMode || PROJECT_CONFIG.mode
const DELAY_TIME = 300

const hostMap = {
    dev: 'http://localhost:3000',
    test: 'https://testdesigner.scm121.com',
    uat: 'https://testdesigner.scm121.com',
    pre: 'https://predesigner.scm121.com',
    prod: 'https://designer.scm121.com'
}

export const host = hostMap[mode || 'dev']

const Content = forwardRef(({ visible, dataSource, onChange, onQuit }, ref) => {
    const readyRef = useRef(false)
    const onChangeRef = useRef(null)
    const onQuitRef = useRef(null)
    const dataSourceRef = useRef(dataSource)
    const [loading, setLoading] = useState(false)

    const { name, messageChannel } = useMessageChannel({ origin: '*' })

    dataSourceRef.current = dataSource
    onChangeRef.current = onChange
    onQuitRef.current = onQuit

    useImperativeHandle(ref, () => ({
        messageChannel
    }))

    useEffect(() => {
        if (visible) {
            messageChannel.postMessage('global/visibilitychange', visible)
        } else {
            setTimeout(() => {
                messageChannel.postMessage('global/visibilitychange', visible)
            }, DELAY_TIME)
        }
    }, [visible])

    // 输入文件
    const inputFiles = () => {
        const images = dataSourceRef.current?.images || []
        transImageToSursung(images.map(_ => _.url)).then(newImages => {
            messageChannel.postMessage('imageEdit/inputFiles', {
                ...dataSourceRef.current,
                images: images.map((_, index) => ({
                    ..._,
                    url: newImages[index]
                }))
            })
        })
    }

    useEffect(() => {
        if (!readyRef.current || !visible) {
            return
        }

        inputFiles()
    }, [visible])

    useEffect(() => {
        const onReady = () => {
            readyRef.current = true
            inputFiles()
            messageChannel.receiveMessage('imageEdit/outputFiles', e => {
                onChangeRef.current && onChangeRef.current(e)
                onQuitRef.current && onQuitRef.current(false)
            })
            messageChannel.receiveMessage('imageEdit/quitEdit', () => {
                onQuitRef.current && onQuitRef.current(true)
            })
            setLoading(false)
        }

        setLoading(true)
        messageChannel.receiveMessage('global/ready', onReady)
    }, [messageChannel])

    return (
        <Spin spinning={loading}>
            <iframe
                className={styles.iframe}
                name={name}
                frameBorder={0}
                src={`${host}?terminalName=${PROJECT_CONFIG.terminalName}&scene=IMAGES_EDITOR&token=${getToken()}`}
                allow="clipboard-read; clipboard-write"
            />
        </Spin>
    )
})

const bodyStyle = {
    padding: 0,
    lineHeight: 0
}

export default ({ visible, dataSource, onChange, onCancel, ...restProps }) => {
    const contentRef = useState(null)

    const handleCancel = block => {
        // 如果为true的话需要检测是否有编辑内容
        if (block) {
            const messageChannel = contentRef.current.messageChannel

            if (!messageChannel) {
                onCancel()
                return
            }

            const handler = hasEdit => {
                messageChannel.removeListen('imageEdit/checkHasEdit', handler)

                if (hasEdit) {
                    Modal.confirm({
                        zIndex: getCurrentModalMaxZindex(),
                        title: '提示',
                        closable: true,
                        content: '是否保存当前编辑内容',
                        cancelText: '不保存',
                        okText: '保存',
                        onOk(close) {
                            close && close()
                            messageChannel.postMessage('imageEdit/saveContent')
                        },
                        onCancel(e) {
                            if (typeof e === 'function') {
                                e()
                                onCancel && onCancel()
                            }
                        }
                    })
                    return
                }

                onCancel()
            }

            messageChannel.receiveMessage('imageEdit/checkHasEdit', handler)
            messageChannel.postMessage('imageEdit/checkHasEdit')
            return
        }

        onCancel()
    }

    return (
        <Drawer title={null} width="92%" visible={visible} bodyStyle={bodyStyle} onClose={handleCancel} {...restProps}>
            <Content
                ref={contentRef}
                visible={visible}
                dataSource={dataSource}
                onChange={onChange}
                onQuit={handleCancel}
            />
        </Drawer>
    )
}
